// ===========================================
// BUSTED! BLACKJACK - ANIMATION QUEUE STORE
// ===========================================
// Zustand store for managing the animation event queue
// Processes events sequentially with configurable timing
// ===========================================

import { create } from 'zustand';
import {
  BlackjackEvent,
  AnimationQueueProcessor,
  AnimationRenderer,
  AnimationTimingConfig,
  DEFAULT_WEB_TIMING,
  FAST_WEB_TIMING,
} from '@busted-blackjack/shared';

// ===========================================
// TYPES
// ===========================================

export type AnimationSpeed = 'normal' | 'fast' | 'instant';

interface AnimationQueueState {
  // Queue state
  pendingCount: number;
  isProcessing: boolean;
  isPaused: boolean;
  lastProcessedSequence: number;
  currentEvent: BlackjackEvent | null;

  // Bet animation state - when true, cards should be hidden until bet animation completes
  betAnimationInProgress: boolean;

  // Per-player hand animation state - when false, chip payout/collection animations are in progress
  // Autobet should wait for this to be true before placing next bet
  myHandAnimationsComplete: boolean;

  // Visual override for active hand index during mid-round payouts
  // When set, UI should show this hand as active instead of the real currentHandIndex
  // Used when a split hand resolves (blackjack/bust) mid-round - keeps visual focus on
  // the resolving hand until its payout animation completes
  activeHandOverride: number | null;

  // Per-seat hand override for opponents (used during split hand clone capture)
  // Maps seat position to the hand index that should be displayed
  opponentHandOverrides: Map<number, number>;

  // Settings
  speed: AnimationSpeed;
  timingConfig: AnimationTimingConfig;

  // Internal processor (not exposed in state)
  _processor: AnimationQueueProcessor;
  _renderer: AnimationRenderer | null;

  // Actions
  enqueue: (event: BlackjackEvent) => void;
  enqueueBatch: (events: BlackjackEvent[]) => void;
  pause: () => void;
  resume: () => void;
  clear: () => void;
  setSpeed: (speed: AnimationSpeed) => void;
  setRenderer: (renderer: AnimationRenderer) => void;
  catchUp: (targetSequence: number) => Promise<void>;
  setBetAnimationInProgress: (inProgress: boolean) => void;
  setMyHandAnimationsComplete: (complete: boolean) => void;
  setActiveHandOverride: (handIndex: number | null) => void;
  /** Incremented when a player action is taken — signals carousel to sync back to active hand */
  actionSyncTrigger: number;
  triggerActionSync: () => void;
  /** Promise that resolves when the current carousel slide animation completes */
  carouselSlidePromise: Promise<void> | null;
  setCarouselSlidePromise: (promise: Promise<void> | null) => void;
  /** When true, carousel slide animations and SFX are suppressed (e.g., during clone capture) */
  suppressCarouselAnimation: boolean;
  setSuppressCarouselAnimation: (suppress: boolean) => void;
  /** Set hand override for a specific opponent seat (for split clone capture) */
  setOpponentHandOverride: (seatPosition: number, handIndex: number | null) => void;
  /** Clear all opponent hand overrides */
  clearOpponentHandOverrides: () => void;
  /** Index of the hand currently being raked (triggers HandLabel animateOut) */
  rakingHandIndex: number | null;
  setRakingHandIndex: (handIndex: number | null) => void;
  /** When true, HandLabel fly-in animations are suppressed (used during rake) */
  suppressLabelFlyIn: boolean;
  setSuppressLabelFlyIn: (suppress: boolean) => void;
  /** When true, outcome label SFX are suppressed (used during rake) */
  suppressOutcomeSfx: boolean;
  setSuppressOutcomeSfx: (suppress: boolean) => void;
  /** When true, outcome labels should have animate=false (used to trigger fly-in for first hand) */
  pendingOutcomeFlyIn: boolean;
  setPendingOutcomeFlyIn: (pending: boolean) => void;
  /** When set, clone capture is in progress - SplitHandDisplay freezes content to this hand index */
  captureFreeze: { seatPosition: number; handIndex: number } | null;
  setCaptureFreeze: (freeze: { seatPosition: number; handIndex: number } | null) => void;
  /** Creates a promise that resolves when dropoutComplete fires (for rake synchronization) */
  createDropoutCompletePromise: () => Promise<void>;
  /** Called when dropoutComplete fires to resolve the waiting promise */
  fireDropoutComplete: () => void;
  /** Internal: resolve callback for dropout promise */
  _dropoutResolve: (() => void) | null;
}

// ===========================================
// STORE
// ===========================================

export const useAnimationQueueStore = create<AnimationQueueState>((set, get) => {
  // Create the processor
  const processor = new AnimationQueueProcessor({
    timingConfig: DEFAULT_WEB_TIMING,
    instantMode: false,
    onEventProcessed: (_event, _duration) => {
      set({ currentEvent: null });
    },
    onQueueStateChange: (pending, processing) => {
      set({
        pendingCount: pending,
        isProcessing: processing,
        lastProcessedSequence: processor.getLastProcessedSequence(),
      });
    },
  });

  return {
    // Initial state
    pendingCount: 0,
    isProcessing: false,
    isPaused: false,
    lastProcessedSequence: -1,
    currentEvent: null,
    betAnimationInProgress: false,
    myHandAnimationsComplete: true, // True by default - no pending animations
    activeHandOverride: null, // No override by default
    opponentHandOverrides: new Map(), // No overrides by default
    actionSyncTrigger: 0,
    carouselSlidePromise: null,
    suppressCarouselAnimation: false,
    rakingHandIndex: null, // No hand raking by default
    suppressLabelFlyIn: false, // Don't suppress by default
    suppressOutcomeSfx: false, // Enable by default - suppress during rake/specific phases
    pendingOutcomeFlyIn: false, // No pending fly-in by default
    captureFreeze: null, // No capture freeze by default
    _dropoutResolve: null, // No pending dropout promise
    speed: 'normal',
    timingConfig: DEFAULT_WEB_TIMING,
    _processor: processor,
    _renderer: null,

    // Actions
    enqueue: (event: BlackjackEvent) => {
      const { _processor } = get();
      set({ currentEvent: event });
      _processor.enqueue(event);
    },

    enqueueBatch: (events: BlackjackEvent[]) => {
      const { _processor } = get();
      _processor.enqueueBatch(events);
    },

    pause: () => {
      const { _processor } = get();
      _processor.pause();
      set({ isPaused: true });
    },

    resume: () => {
      const { _processor } = get();
      _processor.resume();
      set({ isPaused: false });
    },

    clear: () => {
      const { _processor } = get();
      _processor.clear();
      set({
        pendingCount: 0,
        isProcessing: false,
        currentEvent: null,
      });
    },

    setSpeed: (speed: AnimationSpeed) => {
      const { _processor } = get();
      let config: AnimationTimingConfig;
      let instantMode = false;

      switch (speed) {
        case 'fast':
          config = FAST_WEB_TIMING;
          break;
        case 'instant':
          config = DEFAULT_WEB_TIMING;
          instantMode = true;
          break;
        default:
          config = DEFAULT_WEB_TIMING;
      }

      _processor.setTimingConfig(config);
      _processor.setInstantMode(instantMode);
      set({ speed, timingConfig: config });
    },

    setRenderer: (renderer: AnimationRenderer) => {
      const { _processor } = get();
      _processor.setRenderer(renderer);
      set({ _renderer: renderer });
    },

    catchUp: async (targetSequence: number) => {
      const { _processor } = get();
      await _processor.catchUp(targetSequence);
    },

    setBetAnimationInProgress: (inProgress: boolean) => {
      set({ betAnimationInProgress: inProgress });
    },

    setMyHandAnimationsComplete: (complete: boolean) => {
      set({ myHandAnimationsComplete: complete });
    },

    setActiveHandOverride: (handIndex: number | null) => {
      console.log(`[AnimationQueue] setActiveHandOverride: ${handIndex}`);
      set({ activeHandOverride: handIndex });
    },

    triggerActionSync: () => {
      set((state) => ({ actionSyncTrigger: state.actionSyncTrigger + 1 }));
    },

    setCarouselSlidePromise: (promise: Promise<void> | null) => {
      set({ carouselSlidePromise: promise });
    },

    setSuppressCarouselAnimation: (suppress: boolean) => {
      set({ suppressCarouselAnimation: suppress });
    },

    setOpponentHandOverride: (seatPosition: number, handIndex: number | null) => {
      set((state) => {
        const newMap = new Map(state.opponentHandOverrides);
        if (handIndex === null) {
          newMap.delete(seatPosition);
        } else {
          newMap.set(seatPosition, handIndex);
        }
        return { opponentHandOverrides: newMap };
      });
    },

    clearOpponentHandOverrides: () => {
      set({ opponentHandOverrides: new Map() });
    },

    setRakingHandIndex: (handIndex: number | null) => {
      set({ rakingHandIndex: handIndex });
    },

    setSuppressLabelFlyIn: (suppress: boolean) => {
      set({ suppressLabelFlyIn: suppress });
    },

    setSuppressOutcomeSfx: (suppress: boolean) => {
      set({ suppressOutcomeSfx: suppress });
    },

    setPendingOutcomeFlyIn: (pending: boolean) => {
      set({ pendingOutcomeFlyIn: pending });
    },

    setCaptureFreeze: (freeze: { seatPosition: number; handIndex: number } | null) => {
      set({ captureFreeze: freeze });
    },

    createDropoutCompletePromise: () => {
      return new Promise<void>((resolve) => {
        set({ _dropoutResolve: resolve });
      });
    },

    fireDropoutComplete: () => {
      const { _dropoutResolve } = get();
      if (_dropoutResolve) {
        _dropoutResolve();
        set({ _dropoutResolve: null });
      }
    },
  };
});

// ===========================================
// SELECTORS
// ===========================================

export const selectPendingCount = (state: AnimationQueueState) => state.pendingCount;
export const selectIsProcessing = (state: AnimationQueueState) => state.isProcessing;
export const selectCurrentEvent = (state: AnimationQueueState) => state.currentEvent;
export const selectAnimationSpeed = (state: AnimationQueueState) => state.speed;
export const selectBetAnimationInProgress = (state: AnimationQueueState) => state.betAnimationInProgress;
export const selectMyHandAnimationsComplete = (state: AnimationQueueState) => state.myHandAnimationsComplete;
export const selectActiveHandOverride = (state: AnimationQueueState) => state.activeHandOverride;
export const selectActionSyncTrigger = (state: AnimationQueueState) => state.actionSyncTrigger;
export const selectSuppressCarouselAnimation = (state: AnimationQueueState) => state.suppressCarouselAnimation;
export const selectOpponentHandOverrides = (state: AnimationQueueState) => state.opponentHandOverrides;

// ===========================================
// HOOKS
// ===========================================

export function useAnimationQueue() {
  const store = useAnimationQueueStore();
  return {
    enqueue: store.enqueue,
    enqueueBatch: store.enqueueBatch,
    pause: store.pause,
    resume: store.resume,
    clear: store.clear,
    pendingCount: store.pendingCount,
    isProcessing: store.isProcessing,
    currentEvent: store.currentEvent,
  };
}

export function useAnimationSettings() {
  const store = useAnimationQueueStore();
  return {
    speed: store.speed,
    setSpeed: store.setSpeed,
    setRenderer: store.setRenderer,
  };
}

export default useAnimationQueueStore;
